import React from "react";
import styled from "styled-components/macro";

import ImageGallery from "react-image-gallery";
import "react-image-gallery/styles/css/image-gallery.css";

import useWindowSize from "helpers/useWindowSize";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import { COLORS, BREAKPOINTS } from "helpers/designConstants";

const NavButton = styled.button`
  background: none;
  border: 2px solid ${COLORS.secondary};
  border-radius: 50%;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: ${COLORS.text};
  padding: 0;
  position: absolute;
  z-index: 5;
  top: 50%;
  transform: translateY(-50%);

  &:disabled {
    opacity: 0.3;
    cursor: default;
  }

  @media (hover: hover) {
    &:hover:not(:disabled) {
      background: ${COLORS.secondary};
      color: white;
    }
  }

  @media (max-width: ${BREAKPOINTS.mobile}) {
    position: static;
    transform: none;
  }
`;

const MobileNavBar = styled.div`
  display: none;

  @media (max-width: ${BREAKPOINTS.mobile}) {
    display: flex;
    justify-content: center;
    gap: 16px;
    padding: 4px 0;
  }
`;

const DesktopNavLeft = styled(NavButton)`
  left: 10px;

  @media (max-width: ${BREAKPOINTS.mobile}) {
    display: none;
  }
`;

const DesktopNavRight = styled(NavButton)`
  right: 10px;

  @media (max-width: ${BREAKPOINTS.mobile}) {
    display: none;
  }
`;

const ArticleImageGallery = ({ imageList }) => {
  const { width, height } = useWindowSize();
  const [currentIndex, setCurrentIndex] = React.useState(0);
  const galleryRef = React.useRef(null);

  const total = imageList?.length || 0;
  const atFirst = currentIndex === 0;
  const atLast = currentIndex === total - 1;

  return (
    <Content>
      <MobileNavBar>
        <NavButton
          disabled={atFirst && !true}
          onClick={() => galleryRef.current?.slideLeft()}
        >
          <ChevronLeftIcon />
        </NavButton>
        <NavButton
          disabled={atLast && !true}
          onClick={() => galleryRef.current?.slideRight()}
        >
          <ChevronRightIcon />
        </NavButton>
      </MobileNavBar>
      <ImageWrapper shouldBeFullHeight={width - 100 > height}>
        <ImageGallery
          ref={galleryRef}
          lazyLoad={true}
          infinite={true}
          showPlayButton={false}
          showFullscreenButton={false}
          thumbnailPosition={width - 100 > height ? "left" : "bottom"}
          items={imageList}
          onSlide={setCurrentIndex}
          renderLeftNav={(onClick, disabled) => (
            <DesktopNavLeft onClick={onClick} disabled={disabled}>
              <ChevronLeftIcon />
            </DesktopNavLeft>
          )}
          renderRightNav={(onClick, disabled) => (
            <DesktopNavRight onClick={onClick} disabled={disabled}>
              <ChevronRightIcon />
            </DesktopNavRight>
          )}
        />
      </ImageWrapper>
    </Content>
  );
};

const Content = styled.div`
  width: 100%;
  height: 100%;
  overflow: hidden;

  @media (max-width: ${BREAKPOINTS.mobile}) {
    display: flex;
    flex-direction: column;
  }
`;

const ImageWrapper = styled.div`
  height: 100%;
  width: 100%;
  padding: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  .image-gallery {
    width: 100%;
  }

  && .image-gallery-thumbnail {
    border: 2px solid transparent;
    border-radius: 5px;

    .image-gallery-thumbnail-inner {
      padding: 2px;
    }
  }
  && .image-gallery-thumbnail.active {
    border: 2px solid ${COLORS.secondary};
  }

  .image-gallery-content {
    padding: 10px;
    display: ${({ shouldBeFullHeight }) => !shouldBeFullHeight && "grid"};
    grid-template-rows: ${({ shouldBeFullHeight }) =>
      !shouldBeFullHeight && "1fr max-content"};
    overflow: hidden;
  }

  .image-gallery-content.fullscreen {
    background: white;
  }

  && .image-gallery-slide {
    display: flex !important;
    transition: none !important;
  }

  .image-gallery-slide-wrapper {
    height: 100%;
    overflow: hidden;
  }

  .image-gallery,
  .image-gallery-content,
  .image-gallery-swipe,
  .image-gallery-slides,
  .image-gallery-slide {
    height: 100%;
  }

  &&& .image-gallery-image {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }

  .image-gallery-thumbnails-wrapper.bottom {
    overflow: hidden;
  }

  @media (max-width: ${BREAKPOINTS.mobile}) {
    flex: 1;
    min-height: 0;

    .image-gallery {
      max-width: 100%;
      overflow: hidden;
    }

    .image-gallery-content {
      padding: 0;
    }

    .image-gallery-slide-wrapper {
      max-width: 100%;
    }
  }
`;

export default ArticleImageGallery;
